import { useState } from "react";
import axios from "axios";
import { AlertCircle, Loader2, FileSearch, Download, TrendingDown, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" });
};

const formatMonthLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
    timeZone: "Europe/Paris",
  });
};

export default function MissingVsLastMonthButton({ token, month }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = month ? { month } : {};
      const res = await axios.get(`${API}/vigilance/missing-vs-last-month`, {
        headers,
        params,
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la récupération");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen && !data) {
      await fetchData();
    }
  };

  const exportCSV = () => {
    if (!data || !data.clients_missing?.length) return;
    const rows = [["Client", "Nb factures", "Dernière facture", "Total HT", "Total TTC"]];
    data.clients_missing.forEach((c) => {
      const last = c.invoices[0];
      rows.push([
        c.client,
        c.invoice_count,
        last ? last.date : "",
        c.total_ht.toFixed(2),
        c.total_ttc.toFixed(2),
      ]);
    });
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `oublis-facturation-${data.target_month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleOpen(true)}
        className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
        data-testid="missing-vs-last-month-btn"
      >
        <FileSearch className="w-4 h-4 mr-2" />
        Détecter les oublis
      </Button>

      <Dialog open={open} onOpenChange={handleOpen}>
        <DialogContent
          className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="missing-vs-last-month-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              Oublis de facturation détectés
            </DialogTitle>
            <DialogDescription>
              Clients facturés le mois précédent qui n&apos;ont pas encore été
              facturés le mois cible. Comparaison simple et exhaustive.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          )}

          {!loading && data && (
            <>
              {/* Stats banner */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <Calendar className="w-3.5 h-3.5" /> Mois analysé
                    </div>
                    <p className="text-sm font-semibold text-blue-900 mt-1 capitalize">
                      {formatMonthLabel(data.target_month)}
                    </p>
                    <p className="text-[10px] text-blue-700 mt-0.5">
                      vs {formatMonthLabel(data.previous_month)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-orange-700">Clients à relancer</p>
                    <p className="text-2xl font-bold text-orange-900">
                      {data.clients_missing_count}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-red-700">CA à risque (TTC)</p>
                    <p className="text-2xl font-bold text-red-900">
                      {formatCurrency(data.total_at_risk_ttc)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-gray-200 bg-gray-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-600">Volume comparé</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      <TrendingDown className="w-3.5 h-3.5 inline mr-1 text-gray-500" />
                      {data.invoices_current_month_count} / {data.invoices_previous_month_count}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      factures ce mois / mois précédent
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Missing clients table */}
              <div className="flex-1 overflow-auto border rounded-lg">
                {data.clients_missing_count === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="p-3 bg-green-100 rounded-full mb-3">
                      <FileSearch className="w-8 h-8 text-green-600" />
                    </div>
                    <p className="text-lg font-semibold text-green-800">
                      Aucun oubli détecté
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Tous les clients facturés en {formatMonthLabel(data.previous_month)}{" "}
                      ont déjà été facturés en {formatMonthLabel(data.target_month)}.
                    </p>
                  </div>
                ) : (
                  <Table data-testid="missing-clients-table">
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead className="w-[35%]">Client</TableHead>
                        <TableHead className="text-center">
                          Factures ({formatMonthLabel(data.previous_month)})
                        </TableHead>
                        <TableHead>Dernière facture</TableHead>
                        <TableHead className="text-right">Montant HT</TableHead>
                        <TableHead className="text-right">Montant TTC</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.clients_missing.map((c) => {
                        const last = c.invoices[0];
                        return (
                          <TableRow
                            key={c.client}
                            data-testid={`missing-client-row-${c.client}`}
                            className="hover:bg-orange-50/40"
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
                                <span>{c.client}</span>
                              </div>
                              {c.invoice_count > 1 && (
                                <div className="text-[11px] text-muted-foreground mt-1 pl-4">
                                  {c.invoices.slice(0, 3).map((i) => (
                                    <div key={i.numero || i.date}>
                                      {i.numero || "—"} · {formatDate(i.date)} ·{" "}
                                      {formatCurrency(i.total_ttc)}
                                    </div>
                                  ))}
                                  {c.invoices.length > 3 && (
                                    <div className="italic">
                                      +{c.invoices.length - 3} autre(s)
                                    </div>
                                  )}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-orange-50 border-orange-200">
                                {c.invoice_count}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {last && (
                                <div className="text-sm">
                                  <div className="font-mono text-xs text-muted-foreground">
                                    {last.numero || "—"}
                                  </div>
                                  <div>{formatDate(last.date)}</div>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(c.total_ht)}
                            </TableCell>
                            <TableCell className="text-right font-bold text-red-700">
                              {formatCurrency(c.total_ttc)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between gap-2 pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Une comparaison rapide et exhaustive :{" "}
                  <span className="font-medium">
                    présent le mois précédent, absent ce mois-ci
                  </span>
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchData}
                    disabled={loading}
                    data-testid="missing-refresh-btn"
                  >
                    <Loader2 className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : "hidden"}`} />
                    Actualiser
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportCSV}
                    disabled={!data.clients_missing_count}
                    data-testid="missing-export-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Exporter CSV
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
